<template>
  <div id="cnc-icon-page" class="page-wrapper">
    <h1>{{ $t('CncIcon') }}</h1>
    <Icon ref="icon" v-show="false" name="..."/>
    <div class="info-wrapper">
      <div class="total-icon">Total: {{ iconList.reduce((acc, type) => acc + type.icons.length, 0) }}</div>
      <div class="info-desc">
        <CncIcon name="ico_exclamation_circle" :width="16"/>
        <span class="info-desc-text">You can click on the desired icon to instantly copy the code for use.</span>
      </div>
    </div>
    <div class="search-wrapper">
      <el-input v-model="searchIcon" placeholder="Search icon" />
    </div>
    <div class="content-wrapper">
      <div class="tool-bar">
        <span class="tool-bar-title">Setting</span>
        <div class="tool-item">
          <span class="tool-item-label">Background</span>
          <el-switch v-model="isBackground"/>
        </div>
        <div class="tool-item">
          <span class="tool-item-label">Hide New</span>
          <el-switch v-model="isHideNewIcon"/>
        </div>
        <span class="tool-bar-title p-t-20">Properties</span>
        <div class="properties-wrapper">
          <div class="tool-item">
            <span class="tool-item-label">Width</span>
            <el-input v-model="width" :type="number" :maxlength="2"/>
          </div>
          <div class="tool-item">
            <span class="tool-item-label">Gradient</span>
            <el-switch v-model="isGradient"/>
          </div>
          <div v-if="isGradient" class="tool-item">
            <span class="tool-item-label">Outline</span>
            <el-switch v-model="isOutline"/>
          </div>
          <div class="tool-item">
            <span class="tool-item-label">Solid</span>
            <el-switch v-model="isSolid"/>
          </div>
          <div v-if="!isGradient" class="tool-item">
            <span class="tool-item-label">Two Tone</span>
            <el-switch v-model="isTwoTone"/>
          </div>
          <div v-if="!isGradient" class="tool-item">
            <span class="tool-item-label">Fixed Color</span>
            <el-switch v-model="isFixedColor"/>
          </div>
          <div class="tool-item">
            <span class="tool-item-label">Color</span>
            <el-color-picker v-model="color1" />
          </div>
          <div class="tool-item">
            <span class="tool-item-label">Color 2</span>
            <el-color-picker v-model="color2" />
          </div>
          <div v-if="isGradient" class="tool-item">
            <span class="tool-item-label">Color 3</span>
            <el-color-picker v-model="color3" />
          </div>
          <div class="tool-item">
            <span class="tool-item-label">Background</span>
            <el-color-picker v-model="bgColor" />
          </div>
          <div v-if="bgColor" class="tool-item">
            <span class="tool-item-label">Padding</span>
            <el-input v-model="padding" placeholder="8px"/>
          </div>
          <div v-if="bgColor" class="tool-item">
            <span class="tool-item-label">Border Radius</span>
            <el-input v-model="borderRadius" placeholder="999px"/>
          </div>
          <div class="tool-item">
            <span class="tool-item-label">Has State</span>
            <el-switch v-model="hasState"/>
          </div>
          <div v-if="hasState" class="tool-item">
            <span class="tool-item-label">Active</span>
            <el-switch v-model="isActive"/>
          </div>
        </div>
      </div>
      <div class="icon-wrapper">
        <div v-for="type in filteredIconList" :key="type.name" class="icon-list">
          <div v-for="icon in type.icons" :key="icon" class="item-icon" :class="{ 'is-background': isBackground }" @click="handleClickIcon(icon)">
            <CncIcon
              :name="icon"
              :width="width"
              :color="color1"
              :color2="color2"
              :color3="color3"
              :isTwoTone="isTwoTone"
              :isSolid="isSolid"
              :isGradient="isGradient"
              :padding="padding"
              :borderRadius="borderRadius"
              :bgColor="bgColor"
              :hasState="hasState"
              :isActive="isActive"
              :isFixedColor="isFixedColor"
              :isOutline="isOutline"
            />
            <div class="icon-info">
              <span class="txt-name">{{ icon }}</span>
              <span class="txt-type">Type: {{ type.name }}</span>
            </div>
            <span v-if="icon.startsWith('ico_') && !isHideNewIcon" class="txt-is-new-icon">New</span>
            <span class="icon-copy-btn">
              <CncIcon name="ico_square_2" :width="16"/>
            </span>
          </div>
        </div>
        <div v-if="filteredIconList.length === 0" class="no-icon-found">
          <CncIcon name="search" color="#999" />
          <span class="no-icon-found-text">No icon found</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      iconSvg: [],
      iconFantasticon: [],
      iconFontastic: [],
      iconMaterial: [],
      searchIcon: '',
      color1: '#FF2E38',
      color2: '#ffb9b0',
      color3: '#ED1C24',
      isTwoTone: false,
      isSolid: false,
      isGradient: false,
      bgColor: null,
      padding: null,
      borderRadius: null,
      hasState: false,
      isActive: false,
      isBackground: false,
      width: 40,
      isFixedColor: false,
      isHideNewIcon: true,
      isOutline: false
    }
  },
  computed: {
    iconList () {
      return [
        { name: 'SVG', icons: this.iconSvg },
        { name: 'Fantasticon', icons: this.iconFantasticon },
        { name: 'Fontastic', icons: this.iconFontastic },
        { name: 'Material', icons: this.iconMaterial }
      ]
    },
    filteredIconList () {
      const searchKeywords = this.searchIcon.toLowerCase().trim().split(/\s+/)
      if (searchKeywords[0] === '') return this.iconList

      return this.iconList.map(type => ({
        name: type.name,
        icons: type.icons.filter(icon => {
          const iconName = icon.toLowerCase()
          return searchKeywords.every(keyword => iconName.includes(keyword))
        })
      })).filter(type => type.icons.length > 0)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.getIconSvg()
      this.getIconFont()
    })
  },
  watch: {
    'color1' (val) {
      if (!val) this.color1 = '#FF2E38'
    },
    'color2' (val) {
      if (!val) this.color2 = '#ffb9b0'
    },
    'color3' (val) {
      if (!val) this.color3 = '#ED1C24'
    },
    'isTwoTone' (val) {
      if (val) {
        this.isSolid = false
        this.isFixedColor = false
        this.isGradient = false
      }
    },
    'isFixedColor' (val) {
      if (val) {
        this.isTwoTone = false
        this.isSolid = false
        this.isGradient = false
      }
    }
  },
  methods: {
    getIconSvg () {
      const requireComponent = require.context('../../../../../lib/icons/svg-icons', false, /[A-Z]\w+\.(vue|js)$/)
      requireComponent.keys().forEach(fileName => {
        const name = fileName.split('/').pop().replace(/\.\w+$/, '')
        const replaceName = name.replace(/([A-Z][a-z0-9]*)/g, '_$1').replace(/([a-z])([0-9])/g, '$1_$2').toLowerCase().replace(/^_/, '')
        this.iconSvg.push(replaceName)
      })
    },
    getIconFont () {
      const typeList = Object.keys(this.$refs.icon.$data).map(name => ({ name, icons: Object.keys(this.$refs.icon.$data[name]).map(e => e) }))
      this.iconFantasticon = typeList.filter(type => type.name === 'fantasticon').flatMap(e => e.icons)
      this.iconFontastic = typeList.filter(type => type.name === 'fontastic').flatMap(e => e.icons)
      this.iconMaterial = typeList.filter(type => type.name === 'material').flatMap(e => e.icons)
    },
    handleClickIcon (icon) {
      const props = {
        name: icon,
        width: this.width,
        color: this.color1,
        ...(this.isFixedColor ? { isFixedColor: true } : {}),
        ...(this.isTwoTone ? { color2: this.color2 } : {}),
        ...(this.isTwoTone ? { isTwoTone: this.isTwoTone } : {}),
        ...(this.isSolid ? { isSolid: true } : {}),
        ...(this.isGradient ? { isGradient: true } : {}),
        ...(this.bgColor ? { bgColor: this.bgColor } : {}),
        ...(this.padding ? { padding: this.padding } : {}),
        ...(this.borderRadius ? { borderRadius: this.borderRadius } : {}),
        ...(this.hasState ? { hasState: true } : {}),
        ...(this.isActive ? { isActive: true } : {}),
        ...(this.isOutline ? { isOutline: true } : {})
      }
      navigator.clipboard.writeText(`<CncIcon ${Object.entries(props).map(([key, value]) => `${key}="${value}"`).join(' ')} />`)
      this.$message.success('Copied to clipboard')
    }
  }
}
</script>

<style lang="scss">
#cnc-icon-page {
  display: flex;
  flex-direction: column;

  .search-wrapper {
    margin-bottom: 24px;
  }

  .content-wrapper {
    display: flex;
    gap: 24px;
    flex-direction: row;
    align-items: flex-start;
  }

  .tool-bar {
    position: sticky;
    top: 20px; // ระยะห่างจากด้านบนเมื่อ sticky
    max-height: calc(100vh - 40px); // ความสูงสูงสุดของ tool-bar
    overflow-y: auto; // ให้สามารถ scroll ภายใน tool-bar ได้ถ้าเนื้อหายาวเกิน
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    width: 160px;
    min-width: 160px;
    max-width: 160px;
    background-color: $color-cool-grey-50;
    padding: 12px;
    border-radius: 12px;
    flex-direction: column;

    .tool-bar-title {
      font-size: 10px;
      color: $color-cool-grey-400;
      font-weight: 600;
      border-bottom: 1px solid $color-cool-grey-100;
      padding-bottom: 8px;
    }

    .properties-wrapper {
      display: flex;
      flex-direction: row;
      gap: 12px;
      flex-wrap: wrap;
    }

    .tool-item {
      display: flex;
      gap: 8px;
      flex-direction: column;

      .tool-item-label {
        font-size: 12px;
        color: $color-cool-grey-500;
      }
    }
  }

  .info-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .info-desc {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;

      .info-desc-text {
        font-size: 12px;
        color: $color-cool-grey-500;
      }
    }

    .total-icon {
      font-size: 12px;
      color: $color-cool-grey-500;
    }
  }

  .icon-wrapper {
    display: flex;
    flex-direction: row;
    gap: 8px;
    border-radius: 12px;
    flex-wrap: wrap;
    width: 100%;

    .icon-list {
      display: contents;

      .item-icon {
        position: relative;
        display: flex;
        gap: 12px;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 180px;
        border-radius: 12px;
        padding: 12px;
        transition: all 0.3s ease;
        cursor: pointer;

        &.is-background {
          background-color: $color-cool-grey-50;
        }

        .icon-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-items: center;

          .txt-name {
            font-size: 12px;
            color: $color-cool-grey-500;
            width: 150px;
            text-align: center;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          .txt-type {
            font-size: 10px;
            color: $color-cool-grey-400;
          }
        }

        .txt-is-new-icon {
          font-size: 8px;
          background-color: $color-semanticGreen-500;
          color: $color-white;
          padding: 1px 6px;
          border-radius: 20px;
        }

        .icon-copy-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        &:hover {
          background-color: $color-cool-grey-50;

          .txt-name {
            color: $color-cool-grey-900;
          }

          .icon-copy-btn {
            opacity: 1;

            &:hover {
              transform: scale(1.1);
              color: $color-cool-grey-900;
            }
          }
        }
      }
    }

    .no-icon-found {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      flex-direction: column;
      gap: 12px;
      margin-top: 40px;

      .no-icon-found-text {
        font-size: 12px;
        color: $color-cool-grey-400;
      }
    }
  }
}

</style>
